import { motion } from "framer-motion";
import { Server, ShieldCheck, Cloud, Headphones, DatabaseBackup, ArrowUpRight } from "lucide-react";
import { MaskedReveal, FadeUp } from "./MaskedReveal";

const SERVICES = [
  {
    id: "managed-it",
    icon: Server,
    title: "Managed IT",
    span: "lg:col-span-7",
    desc: "Proactive monitoring, patching and strategy for your entire stack. We run your infrastructure as an extension of your team — quietly, reliably, 24/7.",
    image:
      "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tall: true,
  },
  {
    id: "cybersecurity",
    icon: ShieldCheck,
    title: "Cybersecurity",
    span: "lg:col-span-5",
    desc: "Zero-trust architecture, endpoint defense and continuous threat hunting to keep attackers out and auditors happy.",
  },
  {
    id: "cloud",
    icon: Cloud,
    title: "Cloud & Infrastructure",
    span: "lg:col-span-5",
    desc: "Design, migrate and optimize across Azure, AWS and hybrid — scalable, cost-aware and built to grow with you.",
  },
  {
    id: "helpdesk",
    icon: Headphones,
    title: "24/7 Helpdesk",
    span: "lg:col-span-4",
    desc: "Real engineers, on call. Sub-15-minute response with a first-contact resolution rate your team will actually notice.",
  },
  {
    id: "backup-dr",
    icon: DatabaseBackup,
    title: "Backup & DR",
    span: "lg:col-span-3",
    desc: "Immutable backups and tested recovery so a bad day never becomes a bad quarter.",
  },
];

const Card = ({ s, index }) => {
  const Icon = s.icon;
  return (
    <FadeUp
      delay={index * 0.06}
      className={`${s.span} group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0A0A0C] p-8 md:p-10 transition-all duration-500 hover:-translate-y-2 hover:border-[#00E5FF]/40 ${
        s.tall ? "lg:row-span-2 flex flex-col justify-between min-h-[420px]" : "min-h-[240px]"
      }`}
      testId={`service-card-${s.id}`}
    >
      {s.image && (
        <div className="absolute inset-0 z-0">
          <img
            src={s.image}
            alt={s.title}
            loading="lazy"
            className="h-full w-full object-cover opacity-25 mix-blend-luminosity transition-all duration-700 group-hover:opacity-40 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/70 to-[#0A0A0C]/30" />
        </div>
      )}

      <div className="relative z-10 flex items-start justify-between">
        <div className="h-12 w-12 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center text-[#00E5FF]">
          <Icon size={22} />
        </div>
        <ArrowUpRight
          size={20}
          className="text-white/20 transition-all duration-300 group-hover:text-[#00E5FF] group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
        />
      </div>

      <div className="relative z-10 mt-8">
        <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
          {s.title}
        </h3>
        <p className="mt-3 text-[#A0AEC0] leading-relaxed max-w-md">{s.desc}</p>
      </div>
    </FadeUp>
  );
};

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#050505] py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="mb-16 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-[#00E5FF] font-bold mb-6">
              What we operate
            </p>
            <MaskedReveal
              as="h2"
              testId="services-heading"
              lines={["A complete IT", "department, on tap."]}
              className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[0.95]"
            />
          </div>
          <p className="max-w-sm text-[#A0AEC0] leading-relaxed">
            Five disciplines, one accountable partner. No finger-pointing
            between vendors — just outcomes your business can rely on.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 auto-rows-auto">
          {SERVICES.map((s, i) => (
            <Card key={s.id} s={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
